import {
  AbilityBuilder,
  type CreateAbility,
  createMongoAbility,
  type ForcedSubject,
  type MongoAbility,
  type MongoQuery,
} from "@casl/ability";

export { ForbiddenError, type MongoQuery, subject } from "@casl/ability";

// Types for dynamic RBAC and permissions
export type RbacConfig = {
  subjects: Record<string, string>;
  actions: Record<string, string>;
  roles: Record<string, number>;
};

export type PermissionRule = {
  action: string;
  subject: string;
};

export type PermissionsConfig<UserType = unknown> = Record<
  string | number,
  (
    user: UserType,
    builder: AbilityBuilder<AppAbility>,
    rbac: RbacConfig,
  ) => void
>;

// User type is now generic and can be passed dynamically
export type DefaultUser = {
  id: string;
  email: string;
  name: string;
  roleId: number | string;
};

// Dynamic types based on seeded config
type Subjects = string;
type Actions = string;

type AppAbilities = [
  Actions,
  Subjects | ForcedSubject<Exclude<Subjects, "all">>,
];

export type AppAbility = MongoAbility<
  AppAbilities,
  MongoQuery<Record<string, unknown>>
>;
const createAppAbility = createMongoAbility as CreateAbility<AppAbility>;

/**
 * PermissionManager is a generic class for managing RBAC and permissions.
 * Pass your user type as the generic parameter when instantiating.
 */
export class PermissionManager<UserType = DefaultUser> {
  private rbacConfig: RbacConfig;
  private rolePermissionsConfig: PermissionsConfig<UserType>;

  constructor(
    rbacConfig: RbacConfig,
    rolePermissionsConfig: PermissionsConfig<UserType>,
  ) {
    this.rbacConfig = rbacConfig;
    this.rolePermissionsConfig = rolePermissionsConfig;
  }

  /**
   * Returns a CASL AppAbility for the given user.
   */
  defineAbilityFor(user: UserType): AppAbility {
    const builder = new AbilityBuilder(createAppAbility);

    // @ts-expect-error: roleId type is user-defined
    const roleKey = user.roleId;
    const definePermissions = this.rolePermissionsConfig[roleKey];

    if (typeof definePermissions === "function") {
      definePermissions(user, builder, this.rbacConfig);
    } else {
      throw new Error(`Trying to use unknown role "${roleKey}"`);
    }

    return builder.build();
  }
}

/**
 * Creates a CASL ability instance from an array of rules.
 * This is useful when you have serialized rules from an API response.
 */
export function createAbilityFromRules(
  rules: Array<{
    action: string | string[];
    subject: string | string[];
    conditions?: Record<string, unknown>;
  }>,
): AppAbility {
  // Convert rules to CASL format
  const caslRules = rules.map((rule) => ({
    action: Array.isArray(rule.action) ? rule.action : [rule.action],
    subject: Array.isArray(rule.subject) ? rule.subject : [rule.subject],
    conditions: rule.conditions || {},
  }));

  return createAppAbility(caslRules);
}
